import { useState, useCallback, useEffect, useRef } from "react";
import { supabase } from "../config/supabase";
import { useAuth } from "../context/AuthContext";
import { notifyInfo } from "../utils/notify";
import { useFlowContext } from "./useFlowContext";
import { useParametrosPerfil } from "./useParametrosPerfil";

export interface Candidato {
  id: string;
  nome: string;
  matricula: string;
  setor: string;
  votos: number;
  eleicao_id?: string;
  ativo: boolean;
  foto_url?: string;
  created_at: string;
  updated_at: string;
}

export interface Eleicao {
  id: string;
  titulo: string;
  empresa_nome: string;
  empresa_slug: string;
  data_inicio?: string;
  data_fim?: string;
  senha_votacao?: string;
  status: "configurando" | "ativa" | "encerrada";
  total_votos: number;
  created_at: string;
  updated_at: string;
}

export const useVotacao = () => {
  const { user, empresaSelecionada } = useAuth();
  const { context } = useFlowContext();
  const { parametros, podeAdicionarEleitor, getLimiteEleitores } =
    useParametrosPerfil(context.planoUsuario);
  const [candidatos, setCandidatos] = useState<Candidato[]>([]);
  const [eleicoes, setEleicoes] = useState<Eleicao[]>([]);
  const [eleicaoAtiva, setEleicaoAtiva] = useState<Eleicao | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const lastEleicoesRef = useRef<string>("");
  const lastCandidatosRef = useRef<string>("");

  // Carregar candidatos do banco de dados
  const carregarCandidatos = useCallback(
    async (eleicaoId?: string) => {
      if (!user) return;

      try {
        setLoading(true);
        console.log("🔄 Carregando candidatos do banco de dados...");

        // Se há uma eleição ativa, carregar candidatos dessa eleição com contagem de votos
        if (eleicaoId) {
          console.log(
            "🔍 Carregando candidatos da eleição com votos:",
            eleicaoId,
          );

          // Determinar filtro de empresa
          let empresaFilter = {};
          if (empresaSelecionada && user.tipo_usuario !== "SUPER_ADMIN") {
            empresaFilter = { empresa_id: empresaSelecionada.id };
          }

          // Buscar candidatos da eleição (todos os candidatos da eleição, independente do usuario_id)
          const { data: candidatosBanco, error: candidatosError } =
            await supabase
              .from("cp_candidatos")
              .select("*")
              .eq("eleicao_id", eleicaoId)
              .match(empresaFilter)
              .order("created_at", { ascending: false });

          if (candidatosError) {
            console.error("❌ Erro ao carregar candidatos:", candidatosError);
            throw candidatosError;
          }

          // Buscar todos os votos de uma vez e contar por candidato
          const candidatosComVotos = await (async () => {
            if (!candidatosBanco || candidatosBanco.length === 0) return [];

            try {
              // Buscar todos os votos da eleição de uma vez
              const candidatoIds = candidatosBanco.map((c) => c.id);
              console.log(
                "🔄 Consultando cp_votos para candidatos - eleicaoId:",
                eleicaoId,
                "candidatos:",
                candidatoIds.length,
              );
              const { data: votos, error: votosError } = await supabase
                .from("cp_votos")
                .select("candidato_id")
                .eq("eleicao_id", eleicaoId)
                .in("candidato_id", candidatoIds);

              if (votosError) {
                console.warn("⚠️ Erro ao buscar votos:", votosError);
                // Fallback: retornar candidatos com 0 votos
                return candidatosBanco.map((candidato) => ({
                  ...candidato,
                  votos: 0,
                }));
              }

              // Contar votos por candidato
              return candidatosBanco.map((candidato) => {
                const votosCandidato =
                  votos?.filter((v) => v.candidato_id === candidato.id)
                    .length || 0;
                return {
                  ...candidato,
                  votos: votosCandidato,
                };
              });
            } catch (error) {
              console.warn("⚠️ Erro ao processar votos:", error);
              // Fallback: retornar candidatos com 0 votos
              return candidatosBanco.map((candidato) => ({
                ...candidato,
                votos: 0,
              }));
            }
          })();

          console.log(
            "✅ Candidatos carregados com votos:",
            candidatosComVotos,
          );
          setCandidatos(candidatosComVotos);
        } else {
          // Carregar candidatos sem eleição (para cadastro)
          console.log("🔍 Carregando candidatos sem eleição (para cadastro)");

          // Determinar filtro de empresa
          let empresaFilter = {};
          if (empresaSelecionada && user.tipo_usuario !== "SUPER_ADMIN") {
            empresaFilter = { empresa_id: empresaSelecionada.id };
          }

          const { data: candidatosBanco, error: candidatosError } =
            await supabase
              .from("cp_candidatos")
              .select("*")
              .eq("usuario_id", user.id)
              .is("eleicao_id", null)
              .match(empresaFilter)
              .order("created_at", { ascending: false });

          if (candidatosError) {
            console.error("❌ Erro ao carregar candidatos:", candidatosError);
            throw candidatosError;
          }

          console.log("✅ Candidatos carregados:", candidatosBanco);
          setCandidatos(candidatosBanco || []);
        }
      } catch (err) {
        console.error("❌ Erro ao carregar candidatos:", err);
        setError(err instanceof Error ? err.message : "Erro desconhecido");
      } finally {
        setLoading(false);
      }
    },
    [user, empresaSelecionada],
  );

  // Carregar eleições do banco de dados
  const carregarEleicoes = useCallback(async () => {
    if (!user) return;

    try {
      console.log("🔄 Carregando eleições do banco de dados...");

      // Determinar filtro de empresa
      let empresaFilter = {};
      if (empresaSelecionada && user.tipo_usuario !== "SUPER_ADMIN") {
        empresaFilter = { empresa_id: empresaSelecionada.id };
      }

      const { data: eleicoesBanco, error: eleicoesError } = await supabase
        .from("cp_eleicoes")
        .select("*")
        .eq("usuario_id", user.id)
        .match(empresaFilter)
        .order("created_at", { ascending: false });

      if (eleicoesError) {
        console.error("❌ Erro ao carregar eleições:", eleicoesError);
        throw eleicoesError;
      }

      console.log("✅ Eleições carregadas:", eleicoesBanco);
      setEleicoes(eleicoesBanco || []);

      // Encontrar eleição ativa e calcular total de votos correto
      const eleicaoAtivaEncontrada = eleicoesBanco?.find(
        (e) => e.status === "ativa",
      );

      if (eleicaoAtivaEncontrada) {
        // VERIFICAÇÃO DE ENCERRAMENTO AUTOMÁTICO APÓS 15 DIAS (NR-05)
        if (eleicaoAtivaEncontrada.data_inicio) {
          const dataInicio = new Date(eleicaoAtivaEncontrada.data_inicio);
          const agora = new Date();
          const diasDecorridos = Math.floor(
            (agora.getTime() - dataInicio.getTime()) / (1000 * 60 * 60 * 24),
          );

          if (diasDecorridos > 15) {
            console.log(
              "⏰ Eleição excedeu 15 dias - encerrando automaticamente...",
            );

            // Encerrar eleição automaticamente
            const { error: encerrarError } = await supabase
              .from("cp_eleicoes")
              .update({
                status: "encerrada",
                data_fim: new Date().toISOString(),
                motivo_encerramento:
                  "Encerramento automático após 15 dias (conforme NR-05)",
              })
              .eq("id", eleicaoAtivaEncontrada.id);

            if (encerrarError) {
              console.error(
                "❌ Erro ao encerrar eleição automaticamente:",
                encerrarError,
              );
            } else {
              console.log("✅ Eleição encerrada automaticamente após 15 dias");
              notifyInfo(
                "A eleição foi encerrada automaticamente após 15 dias corridos, conforme exigido pela NR-05.",
              );
              // Não definir eleição ativa pois foi encerrada
              setEleicaoAtiva(null);
              // Recarregar eleições para refletir mudança
              const updatedEleicoes = eleicoesBanco?.map((e) =>
                e.id === eleicaoAtivaEncontrada.id
                  ? { ...e, status: "encerrada" as const }
                  : e,
              );
              setEleicoes(updatedEleicoes || []);
              return;
            }
          }
        }

        // Calcular o total de votos correto da eleição ativa
        console.log(
          "🔄 Consultando cp_votos para total da eleição ativa - eleicaoId:",
          eleicaoAtivaEncontrada.id,
        );
        const { count: totalVotosCorreto, error: votosError } = await supabase
          .from("cp_votos")
          .select("*", { count: "exact", head: true })
          .eq("eleicao_id", eleicaoAtivaEncontrada.id);

        if (votosError) {
          console.warn("⚠️ Erro ao contar votos da eleição:", votosError);
        }

        // Atualizar eleição com total correto
        const eleicaoComVotosCorretos = {
          ...eleicaoAtivaEncontrada,
          total_votos: totalVotosCorreto || 0,
        };

        console.log("🔢 Total de votos correto calculado:", totalVotosCorreto);
        setEleicaoAtiva(eleicaoComVotosCorretos);
      } else {
        setEleicaoAtiva(null);
      }
    } catch (err) {
      console.error("❌ Erro ao carregar eleições:", err);
      setError(err instanceof Error ? err.message : "Erro desconhecido");
    }
  }, [user, empresaSelecionada]);

  // Função para salvar foto localmente
  const salvarFotoLocalmente = async (
    file: File,
    matricula: string,
  ): Promise<string> => {
    try {
      console.log("🔄 Salvando foto localmente:", matricula);

      // Obter o ano atual
      const anoAtual = new Date().getFullYear();

      // Nome do arquivo: matricula + ano
      const nomeArquivo = `${matricula}_${anoAtual}.jpg`;

      // Caminho relativo para salvar no banco
      const caminhoRelativo = `/photos_candidate/${nomeArquivo}`;

      console.log("📁 Salvando arquivo:", nomeArquivo);
      console.log("📏 Tamanho do arquivo:", file.size, "bytes");
      console.log("🎯 Tipo do arquivo:", file.type);
      console.log("📍 Caminho relativo:", caminhoRelativo);

      // Salvar no localStorage como base64 (sem tentar salvar no PC)
      const reader = new FileReader();
      const base64Promise = new Promise<string>((resolve, reject) => {
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
      });

      reader.readAsDataURL(file);
      const base64 = await base64Promise;

      // Salvar no localStorage com chave única
      const storageKey = `foto_candidato_${matricula}_${anoAtual}`;
      localStorage.setItem(storageKey, base64);

      console.log("✅ Foto salva no localStorage como base64");
      console.log("🔑 Chave de armazenamento:", storageKey);

      return caminhoRelativo;
    } catch (err) {
      console.error("❌ Erro ao salvar foto localmente:", err);
      throw err;
    }
  };

  // Adicionar candidato com foto
  const adicionarCandidato = async (
    nome: string,
    matricula: string,
    setor: string,
    foto?: File,
  ) => {
    if (!user) return;

    // Verificar se há uma eleição ativa - se houver, não permitir adicionar candidatos
    if (eleicaoAtiva) {
      throw new Error(
        "Não é possível adicionar candidatos durante uma eleição ativa. Encerre a eleição atual primeiro.",
      );
    }

    // Verificar limite de eleitores
    if (!podeAdicionarEleitor(candidatos.length)) {
      const limite = getLimiteEleitores();
      throw new Error(
        `Limite de eleitores atingido. Seu perfil permite até ${limite} eleitores.`,
      );
    }

    try {
      console.log("🔄 Adicionando candidato:", {
        nome,
        matricula,
        setor,
        temFoto: !!foto,
      });

      let fotoUrl: string | undefined;

      // Se há foto, salvar localmente
      if (foto) {
        fotoUrl = await salvarFotoLocalmente(foto, matricula);
      }

      const { data: novoCandidato, error: candidatoError } = await supabase
        .from("cp_candidatos")
        .insert({
          usuario_id: user.id,
          empresa_id: empresaSelecionada?.id || null,
          nome,
          matricula,
          setor,
          votos: 0,
          foto_url: fotoUrl,
        })
        .select()
        .single();

      if (candidatoError) {
        console.error("❌ Erro ao adicionar candidato:", candidatoError);
        throw candidatoError;
      }

      console.log("✅ Candidato adicionado:", novoCandidato);
      setCandidatos((prev) => [novoCandidato, ...prev]);

      return novoCandidato;
    } catch (err) {
      console.error("❌ Erro ao adicionar candidato:", err);
      setError(
        err instanceof Error ? err.message : "Erro ao adicionar candidato",
      );
      throw err;
    }
  };

  // Atualizar foto do candidato
  const atualizarFotoCandidato = async (candidatoId: string, foto: File) => {
    if (!user) return;

    try {
      console.log("🔄 Atualizando foto do candidato:", candidatoId);

      // Buscar candidato para obter a matrícula
      const candidato = candidatos.find((c) => c.id === candidatoId);
      if (!candidato) {
        throw new Error("Candidato não encontrado");
      }

      // Salvar nova foto localmente
      const fotoUrl = await salvarFotoLocalmente(foto, candidato.matricula);

      // Atualizar no banco
      const { error: updateError } = await supabase
        .from("cp_candidatos")
        .update({ foto_url: fotoUrl })
        .eq("id", candidatoId)
        .eq("usuario_id", user.id);

      if (updateError) {
        console.error("❌ Erro ao atualizar foto:", updateError);
        throw updateError;
      }

      console.log("✅ Foto atualizada com sucesso");

      // Atualizar estado local
      setCandidatos((prev) =>
        prev.map((c) =>
          c.id === candidatoId ? { ...c, foto_url: fotoUrl } : c,
        ),
      );
    } catch (err) {
      console.error("❌ Erro ao atualizar foto:", err);
      setError(err instanceof Error ? err.message : "Erro ao atualizar foto");
      throw err;
    }
  };

  // Remover candidato
  const removerCandidato = async (candidatoId: string) => {
    if (!user) return;

    try {
      console.log("🔄 Removendo candidato:", candidatoId);

      const { error: candidatoError } = await supabase
        .from("cp_candidatos")
        .update({ ativo: false })
        .eq("id", candidatoId)
        .eq("usuario_id", user.id);

      if (candidatoError) {
        console.error("❌ Erro ao remover candidato:", candidatoError);
        throw candidatoError;
      }

      console.log("✅ Candidato removido");
      setCandidatos((prev) => prev.filter((c) => c.id !== candidatoId));
    } catch (err) {
      console.error("❌ Erro ao remover candidato:", err);
      setError(
        err instanceof Error ? err.message : "Erro ao remover candidato",
      );
      throw err;
    }
  };

  // Criar nova eleição
  const criarEleicao = async (
    titulo: string,
    senhaVotacao: string,
    empresaNome: string,
    empresaSlug: string,
  ) => {
    if (!user) return;

    try {
      console.log("🔄 Criando nova eleição:", {
        titulo,
        senhaVotacao,
        empresaNome,
        empresaSlug,
      });

      const { data: novaEleicao, error: eleicaoError } = await supabase
        .from("cp_eleicoes")
        .insert({
          usuario_id: user.id,
          empresa_id: empresaSelecionada?.id || null,
          titulo,
          empresa_nome: empresaNome,
          empresa_slug: empresaSlug,
          senha_votacao: senhaVotacao,
          status: "configurando",
          total_votos: 0,
        })
        .select()
        .single();

      if (eleicaoError) {
        console.error("❌ Erro ao criar eleição:", eleicaoError);
        throw eleicaoError;
      }

      console.log("✅ Eleição criada:", novaEleicao);

      // Associar candidatos existentes à nova eleição
      if (candidatos.length > 0) {
        console.log(
          "🔄 Associando candidatos à eleição:",
          candidatos.length,
          "candidatos",
        );

        const { error: updateError } = await supabase
          .from("cp_candidatos")
          .update({ eleicao_id: novaEleicao.id })
          .eq("usuario_id", user.id)
          .is("eleicao_id", null);

        if (updateError) {
          console.error("❌ Erro ao associar candidatos:", updateError);
          throw updateError;
        }

        console.log("✅ Candidatos associados à eleição");
      }

      setEleicoes((prev) => [novaEleicao, ...prev]);

      return novaEleicao;
    } catch (err) {
      console.error("❌ Erro ao criar eleição:", err);
      setError(err instanceof Error ? err.message : "Erro ao criar eleição");
      throw err;
    }
  };

  // Iniciar eleição
  const iniciarEleicao = async (eleicaoId: string) => {
    if (!user) return;

    try {
      console.log("🚨 [INICIAR ELEIÇÃO] Função chamada!");
      console.log("🚨 [INICIAR ELEIÇÃO] Eleição ID:", eleicaoId);
      console.log("🚨 [INICIAR ELEIÇÃO] Stack trace:", new Error().stack);
      console.log("🔄 Iniciando eleição:", eleicaoId);

      // PRIMEIRO: Verificar se há candidatos sem eleicao_id para associar
      console.log("🔍 Verificando candidatos para associar...");
      const { data: candidatosSemEleicao, error: checkError } = await supabase
        .from("cp_candidatos")
        .select("id, matricula")
        .eq("usuario_id", user.id)
        .is("eleicao_id", null);

      if (checkError) {
        console.error("❌ Erro ao verificar candidatos:", checkError);
        throw checkError;
      }

      // Só associar candidatos se houver candidatos sem eleicao_id
      if (candidatosSemEleicao && candidatosSemEleicao.length > 0) {
        console.log(
          "🔗 Associando candidatos à eleição...",
          candidatosSemEleicao.length,
          "candidatos",
        );

        const { error: candidatosError } = await supabase
          .from("cp_candidatos")
          .update({ eleicao_id: eleicaoId })
          .eq("usuario_id", user.id)
          .is("eleicao_id", null);

        if (candidatosError) {
          console.error("❌ Erro ao associar candidatos:", candidatosError);
          throw candidatosError;
        }

        console.log("✅ Candidatos associados à eleição");
      } else {
        console.log("ℹ️ Nenhum candidato sem eleição para associar");
      }

      // SEGUNDO: Ativar a eleição
      console.log("🔄 Ativando eleição...");
      const { error: eleicaoError } = await supabase
        .from("cp_eleicoes")
        .update({
          status: "ativa",
          data_inicio: new Date().toISOString(),
        })
        .eq("id", eleicaoId)
        .eq("usuario_id", user.id);

      if (eleicaoError) {
        console.error("❌ Erro ao iniciar eleição:", eleicaoError);
        throw eleicaoError;
      }

      console.log("✅ Eleição iniciada");

      // TERCEIRO: Limpar candidatos atuais e recarregar dados
      setCandidatos([]); // Limpar candidatos antigos primeiro
      await carregarEleicoes(); // Recarregar eleições (vai definir eleicaoAtiva)
      // O useEffect vai automaticamente carregar os candidatos da eleição ativa
    } catch (err) {
      console.error("❌ Erro ao iniciar eleição:", err);
      setError(err instanceof Error ? err.message : "Erro ao iniciar eleição");
      throw err;
    }
  };

  // Encerrar eleição
  const encerrarEleicao = async (eleicaoId: string) => {
    if (!user) return;

    try {
      console.log("🔄 Encerrando eleição:", eleicaoId);

      const { error: eleicaoError } = await supabase
        .from("cp_eleicoes")
        .update({
          status: "encerrada",
          data_fim: new Date().toISOString(),
        })
        .eq("id", eleicaoId)
        .eq("usuario_id", user.id);

      if (eleicaoError) {
        console.error("❌ Erro ao encerrar eleição:", eleicaoError);
        throw eleicaoError;
      }

      console.log("✅ Eleição encerrada");
      await carregarEleicoes(); // Recarregar para atualizar estado
    } catch (err) {
      console.error("❌ Erro ao encerrar eleição:", err);
      setError(err instanceof Error ? err.message : "Erro ao encerrar eleição");
      throw err;
    }
  };

  // Carregar dados iniciais
  useEffect(() => {
    if (user) {
      const currentKey = `eleicoes-${user.id}-${
        empresaSelecionada?.id || "null"
      }`;

      if (lastEleicoesRef.current !== currentKey) {
        console.log(
          "🔄 useEffect carregarEleicoes - user:",
          user.id,
          "empresa:",
          empresaSelecionada?.id,
        );
        lastEleicoesRef.current = currentKey;
        carregarEleicoes();
      } else {
        console.log(
          "⏭️ Pulando carregamento eleições - mesma chave:",
          currentKey,
        );
      }
    }
  }, [user, empresaSelecionada, carregarEleicoes]);

  // Carregar candidatos quando eleição ativa mudar
  useEffect(() => {
    if (user) {
      const currentKey = `candidatos-${user.id}-${
        empresaSelecionada?.id || "null"
      }-${eleicaoAtiva?.id || "null"}`;

      if (lastCandidatosRef.current !== currentKey) {
        if (eleicaoAtiva) {
          console.log(
            "🔍 Carregando candidatos da eleição ativa:",
            eleicaoAtiva.id,
          );
          carregarCandidatos(eleicaoAtiva.id);
        } else {
          console.log(
            "🔍 Carregando candidatos para cadastro (sem eleição ativa)",
          );
          carregarCandidatos();
        }
        lastCandidatosRef.current = currentKey;
      } else {
        console.log(
          "⏭️ Pulando carregamento candidatos - mesma chave:",
          currentKey,
        );
      }
    }
  }, [user, empresaSelecionada, eleicaoAtiva, carregarCandidatos]);

  // Função para obter URL da foto do candidato
  const obterFotoCandidato = (candidato: Candidato): string | null => {
    if (!candidato.foto_url) return null;

    // Extrair matrícula e ano do caminho da foto
    const match = candidato.foto_url.match(
      /\/photos_candidate\/(\d+)_(\d+)\.jpg/,
    );
    if (!match) return null;

    const [, matricula, ano] = match;
    const storageKey = `foto_candidato_${matricula}_${ano}`;

    // Tentar obter do localStorage
    const base64 = localStorage.getItem(storageKey);
    if (base64) {
      console.log("📸 Foto encontrada no localStorage:", storageKey);
      return base64;
    }

    console.warn("⚠️ Foto não encontrada no localStorage:", storageKey);
    return null;
  };

  // Função para forçar recarregamento dos candidatos
  const recarregarCandidatos = useCallback(
    async (eleicaoId?: string) => {
      console.log("🔄 Forçando recarregamento dos candidatos...");
      await carregarCandidatos(eleicaoId);
    },
    [carregarCandidatos],
  );

  // Função para limpar candidatos duplicados ou com problemas
  const limparCandidatosDuplicados = useCallback(async () => {
    if (!user) return;

    try {
      console.log("🧹 Limpando candidatos duplicados...");

      // Buscar candidatos duplicados (mesma matrícula, mesmo usuário)
      const { data: candidatosDuplicados, error: duplicadosError } =
        await supabase
          .from("cp_candidatos")
          .select("*")
          .eq("usuario_id", user.id)
          .order("created_at", { ascending: false });

      if (duplicadosError) {
        console.error(
          "❌ Erro ao buscar candidatos duplicados:",
          duplicadosError,
        );
        return;
      }

      if (!candidatosDuplicados || candidatosDuplicados.length === 0) {
        console.log("ℹ️ Nenhum candidato encontrado");
        return;
      }

      // Agrupar por matrícula
      const candidatosPorMatricula = candidatosDuplicados.reduce(
        (acc, candidato) => {
          const key = candidato.matricula;
          if (!acc[key]) {
            acc[key] = [];
          }
          acc[key].push(candidato);
          return acc;
        },
        {} as Record<string, any[]>,
      );

      // Para cada matrícula com múltiplos candidatos, manter apenas o mais recente
      for (const [matricula, candidatos] of Object.entries(
        candidatosPorMatricula,
      )) {
        const candidatosArray = candidatos as any[];
        if (candidatosArray.length > 1) {
          console.log(
            `🔍 Matrícula ${matricula} tem ${candidatosArray.length} candidatos`,
          );

          // Ordenar por data de criação (mais recente primeiro)
          const candidatosOrdenados = candidatosArray.sort(
            (a: any, b: any) =>
              new Date(b.created_at).getTime() -
              new Date(a.created_at).getTime(),
          );

          // Manter apenas o primeiro (mais recente) e excluir os demais
          const candidatosParaExcluir = candidatosOrdenados.slice(1);

          for (const candidatoParaExcluir of candidatosParaExcluir) {
            console.log(
              `🗑️ Excluindo candidato duplicado: ${candidatoParaExcluir.nome} (${candidatoParaExcluir.matricula})`,
            );

            const { error: deleteError } = await supabase
              .from("cp_candidatos")
              .delete()
              .eq("id", candidatoParaExcluir.id);

            if (deleteError) {
              console.error(
                "❌ Erro ao excluir candidato duplicado:",
                deleteError,
              );
            } else {
              console.log("✅ Candidato duplicado excluído");
            }
          }
        }
      }

      console.log("✅ Limpeza de candidatos duplicados concluída");
    } catch (error) {
      console.error("❌ Erro ao limpar candidatos duplicados:", error);
    }
  }, [user]);

  // Função para realizar apuração automática
  const realizarApuracao = useCallback(
    async (eleicaoId: string) => {
      if (!user) return null;

      try {
        console.log("📊 Realizando apuração da eleição:", eleicaoId);

        // Buscar candidatos da eleição com contagem de votos
        const { data: candidatosComVotos, error: candidatosError } =
          await supabase
            .from("cp_candidatos")
            .select("*")
            .eq("eleicao_id", eleicaoId)
            .eq("usuario_id", user.id);

        if (candidatosError) {
          console.error(
            "❌ Erro ao buscar candidatos para apuração:",
            candidatosError,
          );
          throw candidatosError;
        }

        if (!candidatosComVotos || candidatosComVotos.length === 0) {
          console.log("ℹ️ Nenhum candidato encontrado para apuração");
          return null;
        }

        // Buscar todos os votos da eleição
        const { data: votos, error: votosError } = await supabase
          .from("cp_votos")
          .select("candidato_id")
          .eq("eleicao_id", eleicaoId);

        if (votosError) {
          console.error("❌ Erro ao buscar votos para apuração:", votosError);
          throw votosError;
        }

        // Contar votos por candidato
        const candidatosComContagem = candidatosComVotos.map((candidato) => {
          const votosCandidato =
            votos?.filter((v) => v.candidato_id === candidato.id).length || 0;
          return {
            ...candidato,
            votos: votosCandidato,
          };
        });

        // Ordenar por número de votos (maior para menor)
        const candidatosOrdenados = candidatosComContagem.sort(
          (a, b) => b.votos - a.votos,
        );

        // Determinar vencedor
        const vencedor = candidatosOrdenados[0];
        const totalVotos = votos?.length || 0;

        const resultadoApuracao = {
          eleicaoId,
          totalVotos,
          totalCandidatos: candidatosComContagem.length,
          vencedor,
          candidatos: candidatosOrdenados,
          dataApuracao: new Date().toISOString(),
        };

        console.log("✅ Apuração concluída:", resultadoApuracao);
        return resultadoApuracao;
      } catch (error) {
        console.error("❌ Erro ao realizar apuração:", error);
        throw error;
      }
    },
    [user],
  );

  // Função para gerar relatório CSV da apuração
  const gerarRelatorioCSV = useCallback((resultadoApuracao: any) => {
    if (!resultadoApuracao) return;

    try {
      console.log("📄 Gerando relatório CSV da apuração...");

      // Cabeçalho do CSV
      const cabecalho = [
        "Posição",
        "Nome do Candidato",
        "Matrícula",
        "Setor",
        "Votos Recebidos",
        "Percentual de Votos",
      ];

      // Dados dos candidatos
      const dados = resultadoApuracao.candidatos.map(
        (candidato: any, index: number) => {
          const percentual =
            resultadoApuracao.totalVotos > 0
              ? (
                  (candidato.votos / resultadoApuracao.totalVotos) *
                  100
                ).toFixed(2)
              : "0.00";

          return [
            index + 1,
            candidato.nome,
            candidato.matricula,
            candidato.setor,
            candidato.votos,
            `${percentual}%`,
          ];
        },
      );

      // Adicionar informações da eleição
      const infoEleicao = [
        [""],
        ["INFORMAÇÕES DA ELEIÇÃO"],
        ["Total de Votos", resultadoApuracao.totalVotos],
        ["Total de Candidatos", resultadoApuracao.totalCandidatos],
        [
          "Data da Apuração",
          new Date(resultadoApuracao.dataApuracao).toLocaleString("pt-BR"),
        ],
        ["Vencedor", resultadoApuracao.vencedor.nome],
        ["Votos do Vencedor", resultadoApuracao.vencedor.votos],
      ];

      // Combinar todos os dados
      const csvContent = [cabecalho, ...dados, ...infoEleicao]
        .map((linha) => linha.join(","))
        .join("\n");

      // Criar e baixar arquivo
      const BOM = "\uFEFF"; // Byte Order Mark para UTF-8
      const csvWithBOM = BOM + csvContent;
      const blob = new Blob([csvWithBOM], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `apuracao_eleicao_${new Date().toISOString().split("T")[0]}.csv`,
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      console.log("✅ Relatório CSV gerado com sucesso");
    } catch (error) {
      console.error("❌ Erro ao gerar relatório CSV:", error);
      throw error;
    }
  }, []);

  // Função para gerar relatório de candidatos de uma eleição
  const gerarRelatorioCandidatos = useCallback(async (eleicaoId: string) => {
    try {
      console.log(
        "📊 Gerando relatório de candidatos para eleição:",
        eleicaoId,
      );

      // Buscar dados da eleição
      const { data: eleicao, error: eleicaoError } = await supabase
        .from("cp_eleicoes")
        .select("titulo, empresa_nome, created_at")
        .eq("id", eleicaoId)
        .single();

      if (eleicaoError) {
        throw eleicaoError;
      }

      // Buscar candidatos da eleição
      const { data: candidatos, error: candidatosError } = await supabase
        .from("cp_candidatos")
        .select("*")
        .eq("eleicao_id", eleicaoId)
        .order("nome");

      if (candidatosError) {
        throw candidatosError;
      }

      if (!candidatos || candidatos.length === 0) {
        notifyInfo("Nenhum candidato encontrado para esta eleição.");
        return;
      }

      // Criar cabeçalho do CSV
      const cabecalho = [
        "Posição",
        "Nome",
        "Email",
        "Telefone",
        "Matrícula",
        "Setor",
        "Cargo",
        "Endereço",
        "Cidade",
        "Estado",
        "CEP",
        "Votos Recebidos",
        "Observações",
        "Data de Cadastro",
      ];

      // Criar linhas de dados
      const linhas = candidatos.map((candidato, index) => [
        index + 1,
        candidato.nome || "",
        candidato.email || "",
        candidato.telefone || "",
        candidato.matricula || "",
        candidato.setor || "",
        candidato.cargo || "",
        candidato.endereco || "",
        candidato.cidade || "",
        candidato.estado || "",
        candidato.cep || "",
        candidato.votos || 0,
        candidato.observacoes || "",
        candidato.created_at
          ? new Date(candidato.created_at).toLocaleDateString("pt-BR")
          : "",
      ]);

      // Combinar cabeçalho e dados
      const csvContent = [cabecalho, ...linhas]
        .map((linha) =>
          linha
            .map((campo) => `"${String(campo).replace(/"/g, '""')}"`)
            .join(","),
        )
        .join("\n");

      // Adicionar metadados no início do arquivo
      const metadados = [
        `Relatório de Candidatos - ${eleicao.titulo}`,
        `Empresa: ${eleicao.empresa_nome}`,
        `Data de Geração: ${new Date().toLocaleDateString(
          "pt-BR",
        )} às ${new Date().toLocaleTimeString("pt-BR")}`,
        `Total de Candidatos: ${candidatos.length}`,
        "",
        "",
      ];

      const csvCompleto = metadados.join("\n") + csvContent;

      // Criar e baixar arquivo
      const BOM = "\uFEFF"; // Byte Order Mark para UTF-8
      const csvWithBOM = BOM + csvCompleto;
      const blob = new Blob([csvWithBOM], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `candidatos_${eleicao.titulo.replace(/[^a-zA-Z0-9]/g, "_")}_${
          new Date().toISOString().split("T")[0]
        }.csv`,
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      console.log("✅ Relatório de candidatos gerado com sucesso");
    } catch (error) {
      console.error("❌ Erro ao gerar relatório de candidatos:", error);
      throw error;
    }
  }, []);

  return {
    candidatos,
    eleicoes,
    eleicaoAtiva,
    loading,
    error,
    adicionarCandidato,
    removerCandidato,
    atualizarFotoCandidato,
    obterFotoCandidato,
    criarEleicao,
    iniciarEleicao,
    encerrarEleicao,
    carregarCandidatos,
    carregarEleicoes,
    recarregarCandidatos,
    limparCandidatosDuplicados,
    realizarApuracao,
    gerarRelatorioCSV,
    gerarRelatorioCandidatos,
  };
};
